import React, { useEffect, useState } from 'react';
import { Alert, Card, Spin, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { AdminService } from '../services/AdminService';

const { Title } = Typography;

interface DailyStats {
  date: string;
  sessionCount: number;
}

const SessionStatsWidget: React.FC = () => {
  const [data, setData] = useState<DailyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const stats = await AdminService.getDailySessionStats(30);

        // Transform data for chart and format dates
        const chartData = stats.map((stat: DailyStats) => ({
          ...stat,
          formattedDate: format(parseISO(stat.date), 'dd MMM', { locale: nl }),
        }));

        setData(chartData);
        setError(null);
      } catch (err) {
        console.error('Error fetching session stats:', err);
        setError('Failed to load session statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert message="Error" description={error} type="error" showIcon />
      </Card>
    );
  }

  const totalSessions = data.reduce((sum, item) => sum + item.sessionCount, 0);

  return (
    <Card>
      <Title level={4}>Sessies per dag (afgelopen maand)</Title>
      <div style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary">Totaal: {totalSessions} sessies</Typography.Text>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="formattedDate"
            tick={{ fontSize: 12 }}
            interval={Math.floor(data.length / 10)} // Show every nth label to avoid crowding
          />
          <YAxis />
          <Tooltip
            formatter={(value: number) => [value, 'Sessies']}
            labelFormatter={(label: string) => `Datum: ${label}`}
          />
          <Bar dataKey="sessionCount" fill="#1890ff" />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default SessionStatsWidget;
